import { RefreshUniversal, TickMarkOutlined, UndoUniversal } from '@duxton-web/icons';
import {
  Alert, Button, Card, Empty, Flex, Input, Modal, Table, Tag, Typography, message,
} from '@duxton-web/ui';
import _ from 'lodash';
import {
  CONTENT_GENERATION_CONFIG_VERSION_STATUS_ACTIVE,
  CONTENT_GENERATION_CONFIG_VERSION_STATUS_DRAFT,
  CONTENT_GENERATION_CONFIG_KEY,
  contentGenerationConfigApply,
  contentGenerationConfigGetLatest,
  contentGenerationConfigListVersions,
  contentGenerationConfigReadFile,
  contentGenerationConfigRollback,
} from 'omega-rtc/actions/ucd';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { formatDateTime, nameFromEmail } from './adapters';
import {
  contentGenerationConfigFileCacheKey,
  contentGenerationConfigVersionNumber,
  findPendingContentGenerationConfigVersion,
  formatConfigFileSize,
  getConfigFileName,
  hasConfigFileChanged,
  normalizeContentGenerationConfigVersionStatus,
  validateConfigFileContent,
} from './ContentGenerationConfig.helpers';
import { EMPTY_CONTENT_CONFIG_PERMISSIONS } from './permissions';
import useContentConfigWrite from './useContentConfigWrite';

const { TextArea } = Input;
const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const VERSION_PAGE_SIZE = 20;

const VERSION_STATUS_TAG = {
  ACTIVE: { color: 'success', label: 'ACTIVE' },
  DRAFT: { color: 'warning', label: 'DRAFT' },
  INACTIVE: { color: 'default', label: 'INACTIVE' },
  UNSPECIFIED: { color: 'default', label: 'UNKNOWN' },
};

const VersionStatusTag = ({ status }) => {
  const statusKey = normalizeContentGenerationConfigVersionStatus(status);
  const tag = VERSION_STATUS_TAG[statusKey];
  return <Tag color={tag.color}>{tag.label}</Tag>;
};

const displayUser = (user) => nameFromEmail(user) || user || '-';

const versionsWithLatest = (versions, latest) => {
  const rows = [...versions];
  if (latest) {
    const latestVersionNumber = contentGenerationConfigVersionNumber(latest.version);
    if (!rows.some((version) => contentGenerationConfigVersionNumber(version.version) === latestVersionNumber)) {
      rows.unshift(latest);
    }
  }
  return rows.sort((left, right) => (
    contentGenerationConfigVersionNumber(right.version) - contentGenerationConfigVersionNumber(left.version)
  ));
};

const snapshotForVersion = (versions, latest, selectedVersion) => {
  const targetVersion = contentGenerationConfigVersionNumber(selectedVersion);
  if (!targetVersion) return latest;
  return versionsWithLatest(versions, latest).find((version) => (
    contentGenerationConfigVersionNumber(version.version) === targetVersion
  )) || latest;
};

const ContentGenerationConfigPage = ({
  contentConfigPermissions = EMPTY_CONTENT_CONFIG_PERMISSIONS,
}) => {
  const dispatch = useDispatch();
  const canUpdate = contentConfigPermissions.canUpdate;
  const userEmail = useSelector((state) => _.get(state, 'app.user.email', ''));
  const latest = useSelector((state) => _.get(state, 'ucd.contentGenerationConfigLatest', null));
  const versions = useSelector((state) => _.get(state, 'ucd.contentGenerationConfigVersions', EMPTY_ARRAY));
  const versionsNextPageToken = useSelector((state) => _.get(
    state,
    'ucd.contentGenerationConfigVersionsNextPageToken',
    '',
  ));
  const latestRequest = useSelector((state) => _.get(
    state,
    'requests.contentGenerationConfigGetLatestRequest',
    EMPTY_OBJECT,
  ));
  const versionsRequest = useSelector((state) => _.get(
    state,
    'requests.contentGenerationConfigListVersionsRequest',
    EMPTY_OBJECT,
  ));
  const readRequest = useSelector((state) => _.get(
    state,
    'requests.contentGenerationConfigReadFileRequest',
    EMPTY_OBJECT,
  ));

  const latestVersion = contentGenerationConfigVersionNumber(_.get(latest, 'version', 0));
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [selectedPath, setSelectedPath] = useState('');
  const [editorContent, setEditorContent] = useState('');
  const [packName, setPackName] = useState('');
  const [comment, setComment] = useState('');
  const [pendingVersionSelection, setPendingVersionSelection] = useState(null);

  const fetchConfig = useCallback(() => {
    dispatch(contentGenerationConfigGetLatest(CONTENT_GENERATION_CONFIG_KEY));
    dispatch(contentGenerationConfigListVersions(
      CONTENT_GENERATION_CONFIG_KEY,
      VERSION_PAGE_SIZE,
    ));
  }, [dispatch]);

  const versionRows = useMemo(() => versionsWithLatest(versions, latest), [versions, latest]);
  const newestKnownVersion = contentGenerationConfigVersionNumber(_.get(versionRows, '[0].version', 0));

  const onWriteSuccess = useCallback(({ operation, previousNewestVersion = 0, targetVersion }) => {
    const successMessage = {
      activate: 'Draft applied.',
      draft: 'Draft saved.',
      rollback: 'Rollback applied.',
    }[operation] || 'Config file updated.';
    message.success(successMessage);
    setComment('');
    if (operation === 'draft') {
      setEditorContent('');
      setPendingVersionSelection({
        status: 'DRAFT',
        newerThanVersion: previousNewestVersion,
      });
      fetchConfig();
      return;
    }
    setPackName('');
    setEditorContent('');
    setSelectedVersion(null);
    setPendingVersionSelection({
      status: 'ACTIVE',
      targetVersion: operation === 'activate' ? targetVersion : null,
      newerThanVersion: previousNewestVersion,
    });
    fetchConfig();
  }, [fetchConfig]);

  const onWriteFailure = useCallback(() => {
    message.error('The config change was not saved. Your local edits are still available.');
  }, []);

  const { busy, runWrite } = useContentConfigWrite({
    onFailure: onWriteFailure,
    onSuccess: onWriteSuccess,
  });

  const selectedSnapshot = useMemo(
    () => snapshotForVersion(versions, latest, selectedVersion),
    [latest, selectedVersion, versions],
  );
  const selectedVersionStatus = normalizeContentGenerationConfigVersionStatus(_.get(selectedSnapshot, 'status', ''));
  const selectedVersionName = _.get(selectedSnapshot, 'name', '');
  const files = _.get(selectedSnapshot, 'files', EMPTY_ARRAY);
  const selectedVersionNumber = contentGenerationConfigVersionNumber(selectedVersion);
  const selectedFileKey = selectedVersionNumber && selectedPath
    ? contentGenerationConfigFileCacheKey(selectedVersionNumber, selectedPath)
    : '';
  const selectedFile = useSelector((state) => _.get(
    state,
    ['ucd', 'contentGenerationConfigFilesByKey', selectedFileKey],
    EMPTY_OBJECT,
  ));
  const selectedFileMeta = files.find((file) => file.path === selectedPath) || EMPTY_OBJECT;
  const selectedFileLoaded = Boolean(selectedFileKey && _.get(selectedFile, 'path', '') === selectedPath);
  const originalContent = selectedFileLoaded ? _.get(selectedFile, 'content', '') : '';
  const contentType = _.get(selectedFile, 'content_type')
    || _.get(selectedFileMeta, 'content_type', 'text/plain');
  const isLatestVersion = latestVersion > 0 && selectedVersionNumber === latestVersion;
  const isLatestActiveVersion = isLatestVersion && selectedVersionStatus === 'ACTIVE';
  const hasEdits = selectedFileLoaded && hasConfigFileChanged(originalContent, editorContent);
  const validation = useMemo(() => validateConfigFileContent({
    content: editorContent,
    contentType,
    path: selectedPath,
  }), [contentType, editorContent, selectedPath]);
  const configRequestPending = Boolean(latestRequest.isPending || versionsRequest.isPending);
  const versionListPending = Boolean(versionsRequest.isPending);
  const loadingInitial = configRequestPending && !latest;
  const loadingFile = Boolean(readRequest.isPending) && !selectedFileLoaded;
  const loadingMoreVersions = Boolean(versionListPending && versionsNextPageToken && versionRows.length > 0);
  const canEditFile = canUpdate && selectedVersionNumber > 0;
  const canApplyActive = canEditFile && isLatestActiveVersion;
  const saveDraftDisabled = !canEditFile
    || !selectedFileLoaded
    || !hasEdits
    || !validation.isValid
    || loadingFile;
  const applyChangesDisabled = !canApplyActive
    || !selectedFileLoaded
    || !hasEdits
    || !validation.isValid
    || loadingFile;

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (!latestVersion) return;
    if (!selectedVersion && !pendingVersionSelection) {
      setSelectedVersion(latestVersion);
    }
  }, [latestVersion, pendingVersionSelection, selectedVersion]);

  useEffect(() => {
    if (!pendingVersionSelection) return;
    const nextVersion = findPendingContentGenerationConfigVersion(versionRows, pendingVersionSelection);
    const nextVersionNumber = contentGenerationConfigVersionNumber(_.get(nextVersion, 'version', 0));
    if (!nextVersionNumber) return;
    setSelectedVersion(nextVersionNumber);
    setPendingVersionSelection(null);
  }, [pendingVersionSelection, versionRows]);

  useEffect(() => {
    if (!files.length) return;
    if (!selectedPath || !files.some((file) => file.path === selectedPath)) {
      setSelectedPath(_.get(files, '[0].path', ''));
    }
  }, [files, selectedPath]);

  useEffect(() => {
    setPackName(selectedVersionName || '');
  }, [selectedVersionName, selectedVersionNumber]);

  const readSelectedFile = useCallback(() => {
    if (!selectedVersionNumber || !selectedPath) return;
    dispatch(contentGenerationConfigReadFile(
      CONTENT_GENERATION_CONFIG_KEY,
      selectedVersionNumber,
      selectedPath,
    ));
  }, [dispatch, selectedPath, selectedVersionNumber]);

  useEffect(() => {
    readSelectedFile();
  }, [readSelectedFile]);

  useEffect(() => {
    setEditorContent('');
  }, [selectedFileKey]);

  useEffect(() => {
    if (!selectedFileLoaded) return;
    setEditorContent(_.get(selectedFile, 'content', ''));
  }, [selectedFile, selectedFileLoaded]);

  const confirmDiscardLocalEdits = useCallback((next) => {
    if (!hasEdits) {
      next();
      return;
    }
    Modal.confirm({
      title: 'Discard local edits?',
      content: 'Your unsaved editor changes will be discarded.',
      okText: 'Discard',
      onOk: () => {
        next();
        return Promise.resolve();
      },
    });
  }, [hasEdits]);

  const selectFile = useCallback((path) => {
    if (path === selectedPath) return;
    confirmDiscardLocalEdits(() => setSelectedPath(path));
  }, [confirmDiscardLocalEdits, selectedPath]);

  const reloadSelectedFile = useCallback(() => {
    confirmDiscardLocalEdits(readSelectedFile);
  }, [confirmDiscardLocalEdits, readSelectedFile]);

  const selectVersion = useCallback((version) => {
    const nextVersion = contentGenerationConfigVersionNumber(version);
    if (!nextVersion || nextVersion === selectedVersionNumber) return;
    confirmDiscardLocalEdits(() => setSelectedVersion(nextVersion));
  }, [confirmDiscardLocalEdits, selectedVersionNumber]);

  const discardLocalEdits = () => setEditorContent(originalContent);

  const saveConfigVersion = (status) => {
    if (!canUpdate) {
      message.error('You do not have permission to perform this action.');
      return;
    }
    const isDraftSave = status === CONTENT_GENERATION_CONFIG_VERSION_STATUS_DRAFT;
    if (!isDraftSave && !canApplyActive) {
      message.error('Switch to the latest active version before applying changes.');
      return;
    }
    if (!selectedPath || !hasEdits) {
      message.error('Make at least one change before saving.');
      return;
    }
    if (!validation.isValid) {
      message.error(validation.error);
      return;
    }
    const trimmedPackName = packName.trim();
    if (isDraftSave && !trimmedPackName) {
      message.error('Enter a pack name before saving a draft.');
      return;
    }
    runWrite(contentGenerationConfigApply({
      configKey: CONTENT_GENERATION_CONFIG_KEY,
      expectedBaseVersion: selectedVersionNumber,
      path: selectedPath,
      contentType,
      content: editorContent,
      user: userEmail || 'unknown',
      comment,
      status,
      name: trimmedPackName,
    }), 'contentGenerationConfigApply', {
      operation: isDraftSave ? 'draft' : 'apply',
      previousNewestVersion: newestKnownVersion,
    });
  };

  const saveDraft = () => saveConfigVersion(CONTENT_GENERATION_CONFIG_VERSION_STATUS_DRAFT);

  const applyChanges = () => saveConfigVersion(CONTENT_GENERATION_CONFIG_VERSION_STATUS_ACTIVE);

  const copyVersionForward = (targetVersion, targetStatus) => {
    if (!canUpdate) {
      message.error('You do not have permission to perform this action.');
      return;
    }
    if (!latestVersion || !targetVersion || targetVersion === latestVersion) return;
    const isDraftVersion = normalizeContentGenerationConfigVersionStatus(targetStatus) === 'DRAFT';
    confirmDiscardLocalEdits(() => {
      Modal.confirm({
        title: isDraftVersion
          ? `Apply draft version ${targetVersion}?`
          : `Rollback to version ${targetVersion}?`,
        content: isDraftVersion
          ? 'This promotes the selected draft version to active.'
          : 'This creates a new active version from the selected historical version.',
        okText: isDraftVersion ? 'Apply' : 'Rollback',
        onOk: () => {
          runWrite(contentGenerationConfigRollback({
            configKey: CONTENT_GENERATION_CONFIG_KEY,
            targetVersion,
            expectedBaseVersion: latestVersion,
            user: userEmail || 'unknown',
            comment: isDraftVersion
              ? `Apply draft version ${targetVersion}`
              : `Rollback to version ${targetVersion}`,
          }), 'contentGenerationConfigRollback', {
            operation: isDraftVersion ? 'activate' : 'rollback',
            previousNewestVersion: newestKnownVersion,
            targetVersion: isDraftVersion ? targetVersion : null,
          });
          return Promise.resolve();
        },
      });
    });
  };

  const renderFileList = () => {
    if (loadingInitial) return <Empty description="Loading files..." />;
    if (!files.length) return <Empty description="No files found." />;

    return (
      <div className="cc-config-file-list">
        {files.map((file) => {
          const path = _.get(file, 'path', '');
          return (
            <button
              key={path}
              type="button"
              className={path === selectedPath
                ? 'cc-config-file-item cc-config-file-item-selected'
                : 'cc-config-file-item'}
              title={path}
              onClick={() => selectFile(path)}
            >
              <span className="cc-config-file-cell">
                <Typography.Body level={2} className="cc-config-file-name">
                  {getConfigFileName(path)}
                </Typography.Body>
                <Typography.Metadata type="secondary" className="cc-config-file-path">
                  {path}
                </Typography.Metadata>
              </span>
              <span className="cc-config-file-meta">
                <Tag>{_.get(file, 'content_type') || 'text/plain'}</Tag>
                <Typography.Metadata type="secondary">
                  {formatConfigFileSize(_.get(file, 'size_bytes', 0))}
                </Typography.Metadata>
              </span>
            </button>
          );
        })}
      </div>
    );
  };

  const refreshConfig = useCallback(() => {
    if (versionListPending) return;
    fetchConfig();
  }, [fetchConfig, versionListPending]);

  const loadMoreVersions = useCallback(() => {
    if (!versionsNextPageToken || versionListPending) return;
    dispatch(contentGenerationConfigListVersions(
      CONTENT_GENERATION_CONFIG_KEY,
      VERSION_PAGE_SIZE,
      versionsNextPageToken,
    ));
  }, [dispatch, versionListPending, versionsNextPageToken]);

  const versionColumns = [
    {
      title: 'Version',
      dataIndex: 'version',
      width: 96,
      render: (version) => <Typography.Body level={2}>v{version}</Typography.Body>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => <VersionStatusTag status={status} />,
    },
    {
      title: 'Pack name',
      dataIndex: 'name',
      width: 180,
      render: (text) => <Typography.Body level={2}>{text || '-'}</Typography.Body>,
    },
    {
      title: 'Updated by',
      dataIndex: 'created_by',
      width: 140,
      render: (user) => <Typography.Body level={2}>{displayUser(user)}</Typography.Body>,
    },
    {
      title: 'Updated on',
      dataIndex: 'created_at',
      width: 180,
      render: (time) => <Typography.Body level={2} type="secondary">{formatDateTime(time)}</Typography.Body>,
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      render: (text) => <Typography.Body level={2} type="secondary">{text || '-'}</Typography.Body>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 128,
      render: (_text, record) => {
        const rowVersion = contentGenerationConfigVersionNumber(record.version);
        const rowStatus = normalizeContentGenerationConfigVersionStatus(record.status);
        const isCurrent = rowVersion === latestVersion && rowStatus === 'ACTIVE';
        const isDraftVersion = rowStatus === 'DRAFT';
        return (
          <Button
            size="small"
            icon={isDraftVersion ? <TickMarkOutlined /> : <UndoUniversal />}
            disabled={!canUpdate || isCurrent || busy || !latestVersion}
            onClick={(event) => {
              event.stopPropagation();
              copyVersionForward(rowVersion, record.status);
            }}
          >
            {isDraftVersion ? 'Apply' : 'Rollback'}
          </Button>
        );
      },
    },
  ];

  return (
    <div className="cc-config-page">
      <Flex vertical gap="middle">
        <Flex justify="space-between" align="center" gap="middle" wrap="wrap">
          <div>
            <Typography.Title level={4} className="cc-title-reset">
              Content Generation Config
            </Typography.Title>
          </div>
          <Button
            disabled={configRequestPending}
            icon={<RefreshUniversal />}
            loading={configRequestPending}
            onClick={refreshConfig}
          >
            Refresh
          </Button>
        </Flex>

        {!canUpdate ? (
          <Alert
            type="info"
            showIcon
            message="You can view content generation config, but cannot edit it."
          />
        ) : null}

        {!latest && !loadingInitial ? (
          <Card>
            <Empty description="No content generation config has been uploaded yet." />
          </Card>
        ) : (
          <React.Fragment>
            <div className="cc-config-workspace">
              <Card title="Files" className="cc-config-files-card">
                {renderFileList()}
              </Card>

              <Card className="cc-config-editor-card">
                {selectedPath ? (
                  <Flex vertical gap="middle">
                    <Flex justify="space-between" align="center" gap="middle" wrap="wrap">
                      <div className="cc-config-path">
                        <Typography.Title level={5} className="cc-title-reset">
                          {getConfigFileName(selectedPath)}
                        </Typography.Title>
                        <Typography.Body level={2} type="secondary">
                          v{selectedVersionNumber || '-'} / {selectedPath}
                        </Typography.Body>
                      </div>
                      <Flex gap={8} wrap="wrap">
                        <Button
                          icon={<RefreshUniversal />}
                          onClick={reloadSelectedFile}
                          loading={Boolean(readRequest.isPending)}
                        >
                          Reload
                        </Button>
                        <Button
                          icon={<UndoUniversal />}
                          disabled={!selectedFileLoaded || !hasEdits || busy}
                          onClick={discardLocalEdits}
                        >
                          Discard
                        </Button>
                        <Button
                          loading={busy}
                          disabled={saveDraftDisabled}
                          onClick={saveDraft}
                        >
                          Save draft
                        </Button>
                        <Button
                          type="primary"
                          icon={<TickMarkOutlined />}
                          loading={busy}
                          disabled={applyChangesDisabled}
                          onClick={applyChanges}
                        >
                          Apply
                        </Button>
                      </Flex>
                    </Flex>

                    {!isLatestActiveVersion ? (
                      <Alert
                        type="warning"
                        showIcon
                        message={
                          `Viewing version ${selectedVersionNumber}. `
                          + 'Changes from this version can be saved as a draft.'
                        }
                      />
                    ) : null}

                    {!validation.isValid ? (
                      <Alert type="error" showIcon message={validation.error} />
                    ) : null}

                    <Flex className="cc-config-editor-fields" gap={8} wrap="wrap">
                      <Input
                        className="cc-config-pack-name"
                        value={packName}
                        onChange={(event) => setPackName(event.target.value)}
                        placeholder="Pack name"
                        disabled={!canEditFile || busy}
                        allowClear
                      />
                      <Input
                        className="cc-config-comment"
                        value={comment}
                        onChange={(event) => setComment(event.target.value)}
                        placeholder="Change note"
                        disabled={!canEditFile || busy}
                        allowClear
                      />
                    </Flex>

                    <TextArea
                      className="cc-config-editor-textarea"
                      value={editorContent}
                      onChange={(event) => setEditorContent(event.target.value)}
                      autoSize={{ minRows: 20, maxRows: 36 }}
                      disabled={!canEditFile || !selectedFileLoaded || loadingFile}
                    />
                  </Flex>
                ) : (
                  <Empty description="Select a config file." />
                )}
              </Card>
            </div>

            <Card title="Version history" className="cc-config-history">
              <Table
                rowKey="version"
                columns={versionColumns}
                dataSource={versionRows}
                loading={Boolean(versionsRequest.isPending) && versionRows.length === 0}
                pagination={false}
                scroll={{ x: 920 }}
                onRow={(record) => ({
                  onClick: () => selectVersion(record.version),
                  className: 'cc-row-clickable',
                })}
                rowClassName={(record) => (
                  contentGenerationConfigVersionNumber(record.version) === selectedVersionNumber
                    ? 'cc-config-row-selected'
                    : ''
                )}
                locale={{ emptyText: 'No versions found.' }}
              />
              {versionsNextPageToken ? (
                <Flex
                  className="cc-config-history-footer"
                  justify="center"
                >
                  <Button
                    disabled={Boolean(versionsRequest.isPending)}
                    loading={loadingMoreVersions}
                    onClick={loadMoreVersions}
                  >
                    Load more
                  </Button>
                </Flex>
              ) : null}
            </Card>
          </React.Fragment>
        )}
      </Flex>
    </div>
  );
};

export default ContentGenerationConfigPage;
